import React, { useContext, useEffect, useRef, useState } from 'react';
import { ReactFormGenerator } from 'react-form-builder2';
import { useNavigate } from 'react-router-dom';

import { isArray } from 'lodash';

import { ia } from 'lib/helpers/utility';

import PersonalInfoForm from 'components/Patient/checkin/PersonalInfo/PersonalInfoForm';
import useUserFormik from 'components/Patient/checkin/PersonalInfo/lib/useUserFormik';
import PersonalIdContents from 'components/practice/NewAppointment/components/Sections/Timeline/components/content/components/PersonalIdContents';
import InsuranceProfiles from 'components/practice/charts/InsuranceProfile/InsuranceProfiles';
import Button from 'components/shared/Buttons/Button';
import { withErrorBoundary } from 'components/shared/Error/Boundary';
import HipaaForm from 'components/shared/Forms/HipaaForm/HipaaForm';
import Icon from 'components/shared/Icon/Icon';
import Modal from 'components/shared/Modal/Modal';
import PreAuthForm from 'components/shared/Modal/PreAuthFormModal/PreAuthForm';
import Popover from 'components/shared/Popovers/Popover/Popover';

import { PatientFormsContext } from '../lib/context';

import '../../checkin/style.scss';

const Actions = ({ data }) => {
  const checkinButton = useRef();
  const navigate = useNavigate();
  const formsIncluded = data?.data?.forms;
  const generatableForms = data?.data?.forms.some((f) => typeof f.id !== 'string');
  const [sortedForms, setSortedForms] = useState([]);

  // useEffect used here to put HIPAA Form always first for preview
  useEffect(() => {
    if (isArray(formsIncluded)) {
      const hipaa = formsIncluded.find((form) => form.id === 'hipaa');
      const otherForms = formsIncluded.filter((form) => form.id !== 'hipaa');

      if (hipaa) {
        setSortedForms([hipaa, ...otherForms]); // Ensure hipaa is first
      } else {
        setSortedForms(otherForms);
      }
    }
  }, [formsIncluded]);

  const [previewPacket, setPreviewPacket] = useState(false);

  // User Data Fetched from Context so it is the same one for all packets which include Personal ID & Demographics Forms
  const { user } = useContext(PatientFormsContext);

  // Redirection function to the instant packet without having to authenticate again by the patient
  const redirectToPacketForm = async () => {
    try {
      navigate(`/ia/${data?.token}`, { state: { fromMyPortal: true } });
    } catch (err) {
      console.error('Failed to copy the link: ', err);
    }
  };

  const renderFilledHipaaForm = (encodedHtml) => {
    const decodedHTML = decodeURIComponent(encodedHtml);
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = decodedHTML;

    // Remove text nodes (so only elements remain)
    Array.from(tempDiv.childNodes).forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE && node.nodeValue.trim() !== '') {
        node.remove();
      }
    });

    tempDiv.querySelectorAll('*').forEach((element) => {
      element.style.cursor = 'not-allowed';
      if (['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON'].includes(element.tagName)) {
        element.setAttribute('disabled', 'true'); // Fully disables interaction
      }
    });

    return (
      <div
        className="HipaaForm p-5"
        style={{ cursor: 'not-allowed' }}
        dangerouslySetInnerHTML={{ __html: tempDiv.innerHTML }}
      />
    );
  };

  const closeModal = () => setPreviewPacket(false);

  const options = [
    {
      label: 'Preview',
      icon: 'eye-mono',
      onClick: () => setPreviewPacket(true)
    },
    { label: 'Fill Forms', icon: 'copy-mono', onClick: redirectToPacketForm }
  ];

  // Already Made Template of User Data used, which will be displayed in Personal ID & Demographics Hard Coded Forms
  const formik = useUserFormik({ user });
  const updatedFormik = { ...formik, intitialErrors: {}, errors: {} }; // Preventing side-effects on required fields since it is read-only | Preview Mode

  const renderHardCodedForms = () => {
    return (
      <>
        {isArray(sortedForms) && // Check if it an array first to avoid crashing
          // Render the hard-coded forms starting from the HIPAA always first, dynamic rendering
          sortedForms.map((form_included) => {
            if (form_included?.id === 'hipaa') {
              if (form_included?.html_data) {
                return renderFilledHipaaForm(form_included?.html_data);
              } else {
                return <HipaaForm key="hipaa" patientView disabled />;
              }
            } else if (form_included?.id === 'pre-auth') {
              return <PreAuthForm key="pre-auth" patientView />;
            } else if (form_included?.id == 'personal-id') {
              return <PersonalIdContents key="personal-id" disabled user={user} />;
            } else if (form_included?.id == 'personal-info') {
              return <PersonalInfoForm patientView key="personal-info" formik={updatedFormik} />;
            } else if (form_included?.id == 'insurance') {
              return (
                <InsuranceProfiles
                  key="insurance"
                  patientId={data?.target_id}
                  myPortalForms={true}
                  previewModal={true}
                />
              );
            }
            return null; // Return none if none is present in the packet
          })}
      </>
    );
  };

  return (
    <>
      <Popover
        className="flex h-full w-full items-center justify-center"
        position="left"
        isFixed
        icon="context-menu-horizontal"
        options={options}
        isBorder={false}
      />
      <Modal
        footer={
          <div className="flex w-full cursor-default justify-between gap-1">
            <Button onClick={closeModal} outlined text="Close" color="neutral" />
          </div>
        }
        slideFromRight
        title="All Forms From This Packet"
        isOpen={previewPacket}
        handleClose={closeModal}
        className="min-w-[1240px] cursor-not-allowed"
        isLarge>
        <div className="pointer-events-none">
          {renderHardCodedForms()}
          {generatableForms ? (
            <div className="relative">
              {ia(data?.form_ids) &&
                data.form_ids.map((form, i) => {
                  return (
                    <ReactFormGenerator
                      key={i}
                      read_only={true}
                      submitButton={
                        <button ref={checkinButton} hidden type="submit">
                          Submit
                        </button>
                      }
                      data={form?.json?.fields}
                      answer_data={form?.response?.json?.fields}
                    />
                  );
                })}
            </div>
          ) : generatableForms ? (
            <div className="flex h-full items-center justify-center">
              <div className="text-center">
                <Icon icon="illustration-empty-files" />
                <p className="text-md text-primary-900">Failed to read forms for this packet</p>
              </div>
            </div>
          ) : null}
        </div>
      </Modal>
    </>
  );
};

export default withErrorBoundary(Actions);
